export interface Location {
  lat: number;
  lng: number;
  address?: string;
  accuracy?: number;
  timestamp?: number;
}

export const locationService = {
  getCurrentPosition: (options?: PositionOptions): Promise<Location> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const defaultOptions: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          try {
            const address = await locationService.reverseGeocode(latitude, longitude);
            resolve({
              lat: latitude,
              lng: longitude,
              address,
              accuracy,
              timestamp: position.timestamp
            });
          } catch (error) {
            // Still resolve with coordinates even if geocoding fails
            resolve({
              lat: latitude,
              lng: longitude,
              accuracy,
              timestamp: position.timestamp
            });
          }
        },
        (error) => {
          reject(locationService.handleGeolocationError(error));
        },
        { ...defaultOptions, ...options }
      );
    });
  },

  // Real reverse geocoding using OpenStreetMap Nominatim (free)
  reverseGeocode: async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      
      if (!response.ok) throw new Error('Geocoding failed');
      
      const data = await response.json();
      return data.display_name || 'Address not available';
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      throw new Error('Could not retrieve address');
    }
  },

  // Alternative: Google Maps Geocoding API (requires API key)
  reverseGeocodeGoogle: async (lat: number, lng: number, apiKey: string): Promise<string> => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );
      
      if (!response.ok) throw new Error('Geocoding failed');
      
      const data = await response.json();
      return data.results[0]?.formatted_address || 'Address not available';
    } catch (error) {
      console.warn('Google geocoding failed:', error);
      throw new Error('Could not retrieve address');
    }
  },

  // Search locations by address
  searchLocations: async (query: string): Promise<Location[]> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      );
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      return data.map((item: any) => ({
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        address: item.display_name
      }));
    } catch (error) {
      console.error('Location search error:', error);
      return [];
    }
  },

  // Calculate distance between two points (in km)
  calculateDistance: (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  },

  // Error handling
  handleGeolocationError: (error: GeolocationPositionError): Error => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error('Location access denied. Please enable location permissions in your browser settings.');
      case error.POSITION_UNAVAILABLE:
        return new Error('Location information is unavailable. Please check your connection.');
      case error.TIMEOUT:
        return new Error('Location request timed out. Please try again.');
      default:
        return new Error('An unknown error occurred while getting your location.');
    }
  },

  // Check if geolocation is supported
  isSupported: (): boolean => {
    return 'geolocation' in navigator;
  },

  // Watch position for real-time updates
  watchPosition: (
    onSuccess: (location: Location) => void, 
    onError?: (error: Error) => void,
    options?: PositionOptions
  ): number => {
    if (!navigator.geolocation) return -1;

    return navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        
        try {
          const address = await locationService.reverseGeocode(latitude, longitude);
          onSuccess({
            lat: latitude,
            lng: longitude,
            address,
            accuracy,
            timestamp: position.timestamp
          });
        } catch (error) {
          onSuccess({
            lat: latitude,
            lng: longitude,
            accuracy,
            timestamp: position.timestamp
          });
        }
      },
      (error) => {
        onError?.(locationService.handleGeolocationError(error));
      },
      options
    );
  },

  // Clear watch
  clearWatch: (watchId: number): void => {
    navigator.geolocation.clearWatch(watchId);
  }
};